import { useState, useRef, useEffect } from 'react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';

const WELCOME = { role: 'assistant', content: "Hi! I'm your Geospatial Portal assistant. Ask me anything about your workflow, jobs, or Site Tracker." };

// Voice goes through the backend TTS proxy (/api/chat/tts) — the ElevenLabs key
// lives only in the backend .env, never in the frontend bundle.
async function speakText(text) {
  if (!text.trim()) return;
  try {
    const { data: blob } = await api.post('/chat/tts', { text }, { responseType: 'blob' });
    const url = URL.createObjectURL(blob);
    const audio = new Audio(url);
    audio.onended = () => URL.revokeObjectURL(url);
    audio.play();
  } catch {}
}

export default function ChatWidget() {
  const { user } = useAuth();
  const canTrain = !!user && !['upload', 'pending'].includes(user.role);

  const [open, setOpen] = useState(false);
  const [view, setView] = useState('chat'); // chat | teach
  const [messages, setMessages] = useState([WELCOME]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [muted, setMuted] = useState(false);
  const [knowledge, setKnowledge] = useState([]);
  const [kbForm, setKbForm] = useState({ title: '', content: '' });
  const [savingKb, setSavingKb] = useState(false);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open && view === 'chat') {
      bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
      inputRef.current?.focus();
    }
  }, [open, messages, view]);

  async function fetchKnowledge() {
    try { const { data } = await api.get('/chat/knowledge'); setKnowledge(data); } catch {}
  }

  function openTeach() {
    setView('teach');
    fetchKnowledge();
  }

  async function teachIt(e) {
    e.preventDefault();
    if (!kbForm.title.trim() || !kbForm.content.trim()) return;
    setSavingKb(true);
    try {
      const { data } = await api.post('/chat/knowledge', kbForm);
      setKnowledge((prev) => [data, ...prev]);
      setKbForm({ title: '', content: '' });
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to save');
    } finally {
      setSavingKb(false);
    }
  }

  async function deleteKnowledge(id) {
    try { await api.delete(`/chat/knowledge/${id}`); setKnowledge((prev) => prev.filter((k) => k.id !== id)); } catch {}
  }

  async function handleSend() {
    const text = input.trim();
    if (!text || loading) return;

    const userMsg = { role: 'user', content: text };
    const nextMessages = [...messages, userMsg];
    setMessages(nextMessages);
    setInput('');
    setLoading(true);

    const assistantMsg = { role: 'assistant', content: '' };
    setMessages((prev) => [...prev, assistantMsg]);

    try {
      const history = nextMessages.slice(1).map((m) => ({ role: m.role, content: m.content }));

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({ messages: history }),
      });

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop();

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const payload = line.slice(6);
          if (payload === '[DONE]') break;
          try {
            const { text, error } = JSON.parse(payload);
            if (error) throw new Error(error);
            if (text) {
              setMessages((prev) => {
                const updated = [...prev];
                updated[updated.length - 1] = {
                  ...updated[updated.length - 1],
                  content: updated[updated.length - 1].content + text,
                };
                return updated;
              });
            }
          } catch {}
        }
      }
    } catch {
      setMessages((prev) => {
        const updated = [...prev];
        updated[updated.length - 1] = { role: 'assistant', content: "Sorry, I'm unavailable right now." };
        return updated;
      });
    } finally {
      setLoading(false);
      if (!muted) {
        setMessages((prev) => {
          const last = prev[prev.length - 1];
          if (last?.role === 'assistant' && last.content) speakText(last.content);
          return prev;
        });
      }
    }
  }

  function handleKey(e) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }

  return (
    <div className="fixed bottom-6 right-6 z-50 flex flex-col items-end gap-3">
      {open && (
        <div
          className="flex flex-col rounded-xl border border-navy-500 shadow-2xl overflow-hidden"
          style={{ backgroundColor: '#0d1b2e', width: '360px', height: '480px' }}
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-navy-600" style={{ backgroundColor: '#080f1a' }}>
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-gold-500 animate-pulse" />
              <span className="text-white text-sm font-bold tracking-wide uppercase">{view === 'teach' ? 'Teach Assistant' : 'BLVD Assistant'}</span>
            </div>
            <div className="flex items-center gap-3">
              {canTrain && (
                <button
                  onClick={() => (view === 'teach' ? setView('chat') : openTeach())}
                  title={view === 'teach' ? 'Back to chat' : 'Teach the assistant'}
                  className={`transition text-sm leading-none ${view === 'teach' ? 'text-gold-500' : 'text-gray-500 hover:text-white'}`}
                >
                  {view === 'teach' ? '💬' : '📚'}
                </button>
              )}
              <button
                onClick={() => setMuted((v) => !v)}
                title={muted ? 'Unmute voice' : 'Mute voice'}
                className="text-gray-500 hover:text-white transition text-sm leading-none"
              >
                {muted ? '🔇' : '🔊'}
              </button>
              <button onClick={() => setOpen(false)} className="text-gray-500 hover:text-white transition text-xl leading-none">&times;</button>
            </div>
          </div>

          {view === 'chat' ? (
            <>
              <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3" style={{ scrollbarWidth: 'thin' }}>
                {messages.map((m, i) => (
                  <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                    <div
                      className={`max-w-xs text-sm rounded-xl px-3 py-2 leading-relaxed whitespace-pre-wrap ${
                        m.role === 'user'
                          ? 'bg-gold-500 text-navy-900 font-medium'
                          : 'text-gray-200 border border-navy-600'
                      }`}
                      style={m.role === 'assistant' ? { backgroundColor: '#142236' } : {}}
                    >
                      {m.content || (loading && i === messages.length - 1 ? '…' : '')}
                    </div>
                  </div>
                ))}
                <div ref={bottomRef} />
              </div>

              <div className="px-3 pb-3 pt-2 border-t border-navy-600">
                <div className="flex gap-2">
                  <textarea
                    ref={inputRef}
                    rows={1}
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={handleKey}
                    placeholder="Ask anything…"
                    disabled={loading}
                    className="flex-1 resize-none rounded-lg border border-navy-500 px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-gold-500 transition"
                    style={{ backgroundColor: '#142236' }}
                  />
                  <button
                    onClick={handleSend}
                    disabled={loading || !input.trim()}
                    className="bg-gold-500 text-navy-900 rounded-lg px-3 font-bold text-sm hover:bg-gold-400 disabled:opacity-40 transition"
                  >
                    ↑
                  </button>
                </div>
                <p className="text-gray-600 text-xs mt-1.5 text-center">Enter to send · Shift+Enter for newline</p>
              </div>
            </>
          ) : (
            <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3" style={{ scrollbarWidth: 'thin' }}>
              <p className="text-gray-400 text-xs leading-relaxed">
                Teach the assistant SOPs, processes, or facts. It will use everything here when answering questions for the whole team.
              </p>

              <form onSubmit={teachIt} className="space-y-2 rounded-lg border border-navy-600 p-3" style={{ backgroundColor: '#142236' }}>
                <input
                  value={kbForm.title}
                  onChange={(e) => setKbForm({ ...kbForm, title: e.target.value })}
                  placeholder="Topic (e.g. How to handle a rush job)"
                  maxLength={160}
                  className="w-full rounded border border-navy-500 px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-gold-500"
                  style={{ backgroundColor: '#0d1b2e' }}
                />
                <textarea
                  value={kbForm.content}
                  onChange={(e) => setKbForm({ ...kbForm, content: e.target.value })}
                  placeholder="The details the assistant should know…"
                  rows={4}
                  className="w-full resize-none rounded border border-navy-500 px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-gold-500"
                  style={{ backgroundColor: '#0d1b2e' }}
                />
                <button
                  type="submit"
                  disabled={savingKb || !kbForm.title.trim() || !kbForm.content.trim()}
                  className="w-full bg-gold-500 text-navy-900 rounded-lg py-2 font-bold text-sm uppercase tracking-wide hover:bg-gold-400 disabled:opacity-40 transition"
                >
                  {savingKb ? 'Saving…' : '📚 Teach It'}
                </button>
              </form>

              <div className="space-y-2">
                <p className="text-gray-500 text-xs uppercase tracking-widest">Taught so far ({knowledge.length})</p>
                {knowledge.length === 0 ? (
                  <p className="text-gray-600 text-xs italic">Nothing yet. Add the first SOP above.</p>
                ) : (
                  knowledge.map((k) => {
                    const canDelete = k.author_id === user?.id || user?.role === 'admin';
                    return (
                      <div key={k.id} className="rounded-lg border border-navy-600 p-3" style={{ backgroundColor: '#142236' }}>
                        <div className="flex items-start justify-between gap-2">
                          <p className="text-white text-sm font-semibold">{k.title}</p>
                          {canDelete && (
                            <button onClick={() => deleteKnowledge(k.id)} title="Delete" className="text-gray-500 hover:text-red-400 text-xs shrink-0">✕</button>
                          )}
                        </div>
                        <p className="text-gray-400 text-xs mt-1 whitespace-pre-wrap leading-relaxed">{k.content}</p>
                        <p className="text-gray-600 text-[10px] mt-2">— {k.author_name}</p>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          )}
        </div>
      )}

      <button
        onClick={() => setOpen((v) => !v)}
        className="w-14 h-14 rounded-full bg-gold-500 text-navy-900 text-xl font-black shadow-lg hover:bg-gold-400 transition flex items-center justify-center"
        title="BLVD Assistant"
      >
        {open ? '✕' : '💬'}
      </button>
    </div>
  );
}
